
import { Sun, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"
import { SleepCellBase } from "./SleepCellBase"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useTheme } from "@/lib/theme/ThemeContext"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface SleepQualityCellProps {
  hoursOfSleep: number | null
}

// Based on baby sleep science:
// - 14+ hours in a single session is unusual and likely indicates issues
// - 24+ hours is physically impossible - must be entry error
const UNUSUAL_SLEEP_HOURS = 14;
const IMPOSSIBLE_SLEEP_HOURS = 24;

export function SleepQualityCell({ hoursOfSleep }: SleepQualityCellProps) {
  const isMobile = useMediaQuery("(max-width: 479px)")
  const isExtraSmall = useMediaQuery("(max-width: 360px)")
  const isLightTheme = false;

  // Handle null/undefined - show TBD
  if (hoursOfSleep === null || hoursOfSleep === undefined) {
    return (
      <div className={cn(
        "px-1 xs:px-1.5 sm:px-2 py-0.5 rounded-full text-2xs xs:text-xs font-medium inline-flex items-center gap-1 border",
        "text-muted-foreground bg-muted/50 border-muted",
        "mobile-truncate-xs"
      )}>
        <Sun className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
        <span className="truncate">TBD</span>
      </div>
    )
  }

  // Check for impossible duration (24+ hours = entry error)
  if (hoursOfSleep >= IMPOSSIBLE_SLEEP_HOURS) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(
              "px-1 xs:px-1.5 sm:px-2 py-0.5 rounded-full text-2xs xs:text-xs font-medium inline-flex items-center gap-1 border cursor-help",
              "text-red-400 bg-red-500/10 border-red-500/30",
              "mobile-truncate-xs"
            )}>
              <AlertTriangle className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
              <span className="truncate">{isExtraSmall ? "Err" : isMobile ? "Check" : "Entry error"}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px] text-center">
            <p className="text-sm">
              The sleep duration seems incorrect. A baby cannot sleep for more than 24 hours straight. Please check the start and end times.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const getQualityColor = (quality: number) => {
    // Light mode has new colors from the image
    if (isLightTheme) {
      if (quality >= 8) return "text-[#308CFD] bg-blue-50 border-blue-200" // Blue from image
      if (quality >= 6) return "text-[#5D91DE] bg-blue-50 border-blue-200" // Light blue from image
      return "text-[#4CCEFD] bg-cyan-50 border-cyan-200" // Cyan from image
    } 
    // Dark mode has brighter colors
    else {
      if (quality >= 8) return "text-[#FFC745] bg-[#000808]"
      if (quality >= 6) return "text-[#FFC745]/90 bg-[#000808]"
      return "text-[#FFC745]/80 bg-[#000808]"
    }
  }

  const getQualityText = (quality: number) => {
    if (isExtraSmall) {
      // Extra short text on smallest screens
      if (quality >= 8) return "Ex"
      if (quality >= 6) return "Gd"
      return "Ok"
    } else if (isMobile) {
      // Short text on mobile
      if (quality >= 8) return "Exc"
      if (quality >= 6) return "Good"
      return "Fair"
    }
    
    // Full text on larger screens
    if (quality >= 8) return "Excellent"
    if (quality >= 6) return "Good"
    return "Fair"
  }

  // Calculate quality based on hours of sleep
  // Cap quality for unusual durations (14-24 hours)
  let quality = 0
  
  if (hoursOfSleep >= UNUSUAL_SLEEP_HOURS) {
    // 14-24 hours is unusual - cap at Fair quality
    quality = 4
  } else if (hoursOfSleep >= 8) quality = 10
  else if (hoursOfSleep >= 7) quality = 8
  else if (hoursOfSleep >= 6) quality = 6
  else if (hoursOfSleep >= 5) quality = 4
  else quality = 2
  
  return (
    <div className={cn(
      "px-1 xs:px-1.5 sm:px-2 py-0.5 rounded-full text-2xs xs:text-xs font-medium inline-flex items-center gap-1 border",
      getQualityColor(quality),
      "mobile-truncate-xs"
    )}>
      <Sun className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
      <span className="truncate">{getQualityText(quality)}</span>
    </div>
  )
}
